import { supabase } from '../lib/supabase';
import FamilyService from './familyService';
import { getTodayBRT, convertUTCtoBRT } from '../utils/dateUtils';
import { isMedicationFinishedByYmd, isMedicationInPauseOn } from '../utils/pauseUtils';
import { logger } from '../utils/logger';

// Timeouts (ms) centralizados para maior resiliência em redes reais
const TIMEOUT_LOGS_MS = 10000;
const TIMEOUT_REMINDERS_MS = 8000;
const TIMEOUT_HEALTH_MS = 8000;

export interface DailyStats {
  medication: {
    taken: number;
    total: number;
    percentage: number;
    pending: number;
    byMember: Array<{
      memberId: string;
      memberName: string;
      taken: number;
      total: number;
      percentage: number;
    }>;
  };
  
  reminders: {
    completed: number;
    pending: number;
    total: number;
  };
  
  points: {
    earnedToday: number;
    totalFamily: number;
  };
  
  health: {
    positiveEvents: number;
    negativeEvents: number;
    symptoms: number;
  };
  
  streaks: {
    activeMembersCount: number;
    longestStreak: number;
  };
  
  quiz: {
    completedCount: number;
    totalMembers: number;
  };
  
  adherence: {
    overall: number;
    byMember: Array<{
      memberId: string;
      memberName: string;
      percentage: number;
      status: 'excellent' | 'good' | 'warning' | 'none';
    }>;
  };
}

class DailyStatsService {
  private static instance: DailyStatsService;
  private familyService = FamilyService.getInstance();

  private constructor() {}

  static getInstance(): DailyStatsService {
    if (!DailyStatsService.instance) {
      DailyStatsService.instance = new DailyStatsService();
    }

    return DailyStatsService.instance;
  }

  // Alguns drivers podem retornar times como string JSON ('["09:00"]').
  // Esta função normaliza para array de strings.
  private getTimesArray(times: any): string[] {
    // Caso já seja array (SDK JS do Supabase)
    if (Array.isArray(times)) {
      return (times as any[])
        .map((t) => {
          const s = typeof t === 'string' ? t : String(t);
          const m = s.match(/\b([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?\b/);
          if (!m) return null;
          const hh = m[1].padStart(2, '0');
          const mm = m[2];
          return `${hh}:${mm}`;
        })
        .filter((t): t is string => typeof t === 'string');
    }

    // Caso venha como string
    if (typeof times === 'string') {
      const s = times.trim();
      // 1) JSON: '["09:00","21:00"]'
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) {
          return parsed
            .map((t: any) => {
              const str = typeof t === 'string' ? t : String(t);
              const m = str.match(/\b([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?\b/);
              if (!m) return null;
              const hh = m[1].padStart(2, '0');
              const mm = m[2];
              return `${hh}:${mm}`;
            })
            .filter((t: any): t is string => typeof t === 'string');
        }
      } catch { /* segue para outros formatos */ }

      // 2) Formato Postgres: '{09:00,21:00}' (text[])
      if (s.startsWith('{') && s.endsWith('}')) {
        const body = s.slice(1, -1);
        return body
          .split(',')
          .map(p => p.trim().replace(/^"|"$/g, ''))
          .map(str => {
            const m = str.match(/\b([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?\b/);
            if (!m) return null;
            const hh = m[1].padStart(2, '0');
            const mm = m[2];
            return `${hh}:${mm}`;
          })
          .filter((t): t is string => typeof t === 'string');
      }

      // 3) Qualquer string: extrair tokens HH:mm[(:ss)]
      const matches = s.match(/\b([01]?\d|2[0-3]):[0-5]\d(?::[0-5]\d)?\b/g);
      if (matches) {
        return matches.map((tok) => {
          const m = tok.match(/\b([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?\b/);
          if (!m) return '';
          const hh = m[1].padStart(2, '0');
          const mm = m[2];
          return `${hh}:${mm}`;
        }).filter(Boolean);
      }

      return [];
    }

    return [];
  }

  async getFamilyDailyStats(familyId: string): Promise<DailyStats> {
    logger.info('📊 [DailyStats] Calculando estatísticas para família:', familyId);
    
    const today = getTodayBRT();
    logger.info('📅 [DailyStats] Data de hoje (BRT):', today);
    
    try {
      // Buscar membros da família
      logger.info('📊 [DailyStats] Buscando membros...');
      const members = await this.familyService.getFamilyMembers(familyId);
      logger.info('👥 [DailyStats] Membros encontrados:', members.length);
      
      // Buscar medicações ativas de todos os membros
      logger.info('📊 [DailyStats] Buscando medicações...');
      const allMedications = await this.getAllFamilyMedications(members);

const todayLocal = new Date(`${today}T12:00:00-03:00`);
const parseLocalYmd = (ymd?: string | null): Date | null => {
  if (!ymd) return null;
  const parts = ymd.split('-').map(p => Number(p));
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  const [y, m, d] = parts;
  return new Date(y, (m || 1) - 1, d || 1);
};

// Filtrar: já iniciadas e não finalizadas (considera end_date e fim planejado)
const validMedications = allMedications.filter((m: any) => {
  const s = parseLocalYmd(m.start_date);
  if (s && todayLocal < s) return false;
  if (isMedicationFinishedByYmd(m, today)) return false;
  return true;
});

// Excluir pausadas hoje para estatísticas diárias
const medicationsForToday = validMedications.filter((m: any) => !isMedicationInPauseOn(m, today));
      logger.info('💊 [DailyStats] Medicações consideradas hoje (não pausadas):', medicationsForToday.length);
      
      // Buscar logs de hoje
      logger.info('📊 [DailyStats] Buscando logs de medicação...');
      const { logs: todayLogs, logsData } = await this.getTodayMedicationLogs(today);
      logger.info('📋 [DailyStats] Logs de hoje:', todayLogs.length);
      
      // Buscar lembretes de hoje
      logger.info('📊 [DailyStats] Buscando lembretes...');
      const todayReminders = await this.getTodayReminders(members, today);
      logger.info('🔔 [DailyStats] Lembretes de hoje:', todayReminders.total);
      
      // Buscar eventos de saúde de hoje
      logger.info('📊 [DailyStats] Buscando eventos de saúde...');
      const healthEvents = await this.getTodayHealthEvents(members, today);
      logger.info('💚 [DailyStats] Eventos de saúde:', healthEvents);
      
      // Buscar pontos ganhos hoje
      logger.info('📊 [DailyStats] Buscando pontos...');
      const pointsToday = await this.getTodayPoints(members, today);
      logger.info('⭐ [DailyStats] Pontos hoje:', pointsToday);
      
      // Buscar pontos da família (curiosidades)
      logger.info('📊 [DailyStats] Buscando pontos da família...');
      const familyPoints = await this.getFamilyPoints(familyId);
      logger.info('👨‍👩‍👧‍👦 [DailyStats] Pontos da família:', familyPoints);
      
      // Buscar streaks ativos
      logger.info('📊 [DailyStats] Buscando streaks...');
      const activeStreaks = await this.getActiveStreaks(members);
      logger.info('🔥 [DailyStats] Streaks ativos:', activeStreaks);
      
      // Buscar quiz completado hoje
      logger.info('📊 [DailyStats] Buscando quiz stats...');
      const quizStats = await this.getTodayQuizStats(members, today);
      logger.info('🎯 [DailyStats] Quiz completado:', quizStats);
      
      // Calcular estatísticas
      logger.info('📊 [DailyStats] Calculando estatísticas finais...');
      const stats = this.calculateStats({
        members,
        medications: medicationsForToday,
        logs: todayLogs,
        logsData,
        reminders: todayReminders,
        healthEvents,
        pointsToday,
        familyPoints,
        activeStreaks,
        quizStats
      });
      
      logger.info('✅ [DailyStats] Estatísticas calculadas com sucesso!');
      return stats;
      
    } catch (error) {
      logger.error('❌ [DailyStats] Erro ao calcular estatísticas:', error);
      throw error;
    }
  }

  private async getAllFamilyMedications(members: any[]): Promise<any[]> {
    const allMedications: any[] = [];
    
    for (const member of members) {
      try {
        const medications = await this.familyService.getMemberMedications(member.id);
        const activeMeds = medications.filter((m: any) => m.is_active !== false);
        allMedications.push(...activeMeds.map((m: any) => ({ ...m, member_id: member.id, member_name: member.name })));
      } catch (error) {
        logger.error(`Erro ao buscar medicações do membro ${member.id}:`, error);
      }
    }
    
    return allMedications;
  }

  private async getTodayMedicationLogs(today: string): Promise<{ logs: any[], logsData: any[] }> {
    try {
      logger.info('📋 [DailyStats] Buscando logs de medicação de hoje (v2)...', today);
      
      // CORRIGIDO: Buscar TODOS os logs que podem ter doses de hoje no doses_detail
      // Não filtrar apenas por created_at, pois doses podem ser registradas em dias diferentes
      const queryPromise = supabase
        .from('medication_logs_v2')
        .select('medication_id, member_id, time_slot, taken, taken_late, created_at, doses_detail');
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout ao buscar logs')), TIMEOUT_LOGS_MS)
      );
      
      const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as any;

      if (error) {
        logger.error('❌ [DailyStats] Erro ao buscar logs:', error);
        return { logs: [], logsData: [] };
      }
      
      logger.info('📋 [DailyStats] Logs encontrados:', data?.length || 0);
      
      // Calcular doses tomadas hoje usando doses_detail JSONB para maior precisão
      let takenToday = 0;
      const todayLogsData: any[] = [];
      
      if (data && data.length > 0) {
        data.forEach((log: any) => {
          let hasTodayDoses = false;
          
          // Usar doses_detail se disponível (mais preciso)
          if (log.doses_detail && typeof log.doses_detail === 'object') {
            const dosesDetail = log.doses_detail;
            
            // Iterar sobre as doses no JSONB
            Object.values(dosesDetail).forEach((dose: any) => {
              if (dose && dose.taken_at) {
                const doseDate = convertUTCtoBRT(dose.taken_at);
                if (doseDate === today && (dose.status === 'taken' || dose.status === 'taken_late')) {
                  takenToday++;
                  hasTodayDoses = true;
                }
              }
            });
          } else {
            // Fallback: verificar se o log foi criado hoje e tem doses tomadas
            const logDate = convertUTCtoBRT(log.created_at);
            if (logDate === today) {
              takenToday += (log.taken || 0) + (log.taken_late || 0);
              hasTodayDoses = true;
            }
          }
          
          // Incluir log se tem doses de hoje
          if (hasTodayDoses) {
            todayLogsData.push(log);
          }
        });
      }
      
      logger.info('✅ [DailyStats] Logs com doses de hoje:', todayLogsData.length);
      logger.info('✅ [DailyStats] Doses tomadas hoje (filtradas):', takenToday);
      
      // Retornar array com número de doses tomadas (para compatibilidade) + dados filtrados
      return {
        logs: Array(takenToday).fill({ status: 'taken' }),
        logsData: todayLogsData
      };
    } catch (error) {
      logger.error('❌ [DailyStats] Erro ao buscar logs (timeout ou erro):', error);
      return { logs: [], logsData: [] };
    }
  }

  private async getTodayReminders(members: any[], today: string): Promise<any> {
    const currentDayOfWeek = new Date().getDay();
    let totalReminders = 0;
    const completedReminders = 0;
    
    logger.info('🔔 [DailyStats] Buscando lembretes de', members.length, 'membros');
    
    for (const member of members) {
      try {
        const queryPromise = supabase
          .from('reminders' as any)
          .select('*')
          .eq('member_id', member.id)
          .eq('is_active', true);
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), TIMEOUT_REMINDERS_MS)
        );
        
        const { data: reminders } = await Promise.race([queryPromise, timeoutPromise]) as any;

        if (reminders) {
          for (const reminder of reminders) {
            // Lembrete único para hoje
            if (reminder.reminder_date === today) {
              totalReminders++;
            }
            // Lembrete recorrente para hoje
            else if (reminder.days_of_week?.includes(currentDayOfWeek)) {
              totalReminders++;
            }
          }
        }
      } catch (error) {
        logger.error('❌ [DailyStats] Erro ao buscar lembretes:', error);
      }
    }
    
    logger.info('✅ [DailyStats] Total de lembretes:', totalReminders);
    return { total: totalReminders, completed: completedReminders, pending: totalReminders - completedReminders };
  }

  private async getTodayHealthEvents(members: any[], today: string): Promise<any> {
    let positiveEvents = 0;
    let negativeEvents = 0;
    
    logger.info('💚 [DailyStats] Buscando eventos de saúde de', members.length, 'membros');
    
    for (const member of members) {
      try {
        // Atualizado: usar view unificada health_journal_view (health_events ∪ unified_point_history)
        const queryPromise = supabase
          .from('health_journal_view' as any)
          .select('member_id, created_at, event_type')
          .eq('member_id', member.id)
          .order('created_at', { ascending: false })
          .limit(500);
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), TIMEOUT_HEALTH_MS)
        );
        
        const { data: events } = await Promise.race([queryPromise, timeoutPromise]) as any;

        if (events) {
          const todayEvents = events.filter((event: any) => convertUTCtoBRT(event.created_at) === today);
          positiveEvents += todayEvents.filter((e: any) => e.event_type === 'positive').length;
          negativeEvents += todayEvents.filter((e: any) => e.event_type === 'negative').length;
        }
      } catch (error) {
        logger.error('❌ [DailyStats] Erro ao buscar eventos de saúde:', error);
      }
    }
    
    logger.info('✅ [DailyStats] Eventos (view unificada): +', positiveEvents, '/ -', negativeEvents);
    return { positiveEvents, negativeEvents, symptoms: negativeEvents };
  }

  private async getFamilyPoints(familyId: string): Promise<number> {
    logger.info('👨‍👩‍👧‍👦 [DailyStats] Buscando pontos da família (curiosidades)...');
    
    try {
      const { data, error } = await supabase
        .from('family_points')
        .select('total_points')
        .eq('family_id', familyId)
        .single();
      
      if (error) {
        logger.error('❌ [DailyStats] Erro ao buscar family_points:', error);
        return 0;
      }
      
      const points = data?.total_points || 0;
      logger.info('✅ [DailyStats] Pontos da família:', points);
      return points;
      
    } catch (error) {
      logger.error('❌ [DailyStats] Erro ao buscar family_points:', error);
      return 0;
    }
  }

  private async getTodayPoints(members: any[], today: string): Promise<number> {
    let totalPoints = 0;
    
    logger.info('⭐ [DailyStats] Buscando pontos de', members.length, 'membros');
    logger.info('📅 [DailyStats] Data BRT para filtro:', today);
    
    for (const member of members) {
      try {
        // CORRIGIDO: Usar member_points_history em vez de point_events (obsoleto)
        // Buscar TODOS os eventos e filtrar por data BRT no código
        const queryPromise = supabase
          .from('member_points_history')
          .select('difference, changed_at')
          .eq('member_id', member.id)
          .order('changed_at', { ascending: false });
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 2000)
        );
        
        const { data: points, error } = await Promise.race([queryPromise, timeoutPromise]) as any;

        if (error) {
          logger.error(`❌ [DailyStats] Erro ao buscar pontos do membro ${member.id}:`, error);
          continue;
        }

        if (points) {
          // Filtrar eventos que aconteceram HOJE em BRT
          const todayPoints = points.filter((p: any) => {
            const eventDateBRT = convertUTCtoBRT(p.changed_at);
            return eventDateBRT === today;
          });
          
          const memberPoints = todayPoints.reduce((sum: number, p: any) => sum + (p.difference || 0), 0);
          logger.info(`💰 [DailyStats] ${member.name}: ${memberPoints} pontos hoje (${todayPoints.length} eventos)`);
          totalPoints += memberPoints;
        }
      } catch (error) {
        logger.error('❌ [DailyStats] Erro ao buscar pontos:', error);
      }
    }
    
    logger.info('✅ [DailyStats] Total de pontos hoje:', totalPoints);
    return totalPoints;
  }

  private async getActiveStreaks(members: any[]): Promise<any> {
    logger.info('🔥 [DailyStats] Buscando streaks de', members.length, 'membros');

    if (members.length === 0) {
      logger.info('⚠️ [DailyStats] Nenhum membro para calcular streaks');
      return { activeMembersCount: 0, longestStreak: 0 };
    }

    const memberIds = members.map((member: any) => member.id);

    try {
      const { data, error } = await supabase
        .from('member_points_v2')
        .select('member_id, current_treatment_streak')
        .in('member_id', memberIds);

      if (error) {
        logger.error('❌ [DailyStats] Erro ao buscar streaks ativos:', error);
        return { activeMembersCount: 0, longestStreak: 0 };
      }

      const streakValues = (data || []).map((row: any) => row.current_treatment_streak || 0);
      const activeMembersCount = streakValues.filter((streak: number) => streak > 0).length;
      const longestStreak = streakValues.length > 0 ? Math.max(...streakValues, 0) : 0;

      logger.info('🔥 [DailyStats] Membros com streak ativo:', activeMembersCount, 'Maior streak:', longestStreak);

      return { activeMembersCount, longestStreak };
    } catch (error) {
      logger.error('❌ [DailyStats] Falha inesperada ao calcular streaks:', error);
      return { activeMembersCount: 0, longestStreak: 0 };
    }
  }

  private async getTodayQuizStats(members: any[], today: string): Promise<any> {
    let completedCount = 0;
    
    logger.info('🎯 [DailyStats] Verificando quiz de', members.length, 'membros');
    
    for (const member of members) {
      try {
        logger.info(`🎯 [DailyStats] Buscando quiz de ${member.name}...`);
        
        // Buscar tentativas de resposta do quiz e filtrar pelo dia em BRT
        const { data: attempts, error } = await supabase
          .from('quiz_answer_attempts' as any)
          .select('id, question_id, created_at')
          .eq('member_id', member.id)
          .order('created_at', { ascending: false })
          .limit(200);

        if (error) {
          logger.error(`❌ [DailyStats] Erro ao buscar quiz do membro ${member.id}:`, error);
          continue;
        }

        const todayAttempts = (attempts || []).filter((attempt: any) => convertUTCtoBRT(attempt.created_at) === today);
        logger.info(`✅ [DailyStats] ${member.name}: ${todayAttempts.length} respostas de quiz hoje`);
        
        // Se respondeu pelo menos 3 perguntas hoje, considera como completado
        if (todayAttempts.length >= 3) {
          completedCount++;
          logger.info(`🎯 [DailyStats] ${member.name}: Quiz completado!`);
        }
      } catch (error) {
        logger.error(`❌ [DailyStats] Erro ao buscar quiz de ${member.name}:`, error);
      }
    }
    
    logger.info(`🎯 [DailyStats] Total: ${completedCount}/${members.length} completaram quiz`);
    return { completedCount, totalMembers: members.length };
  }

  private calculateStats(data: any): DailyStats {
    const { members, medications, logs, logsData } = data;
    const today = getTodayBRT();
    
    // Calcular doses totais (cada medicação tem N horários por dia)
    const totalDoses = medications.reduce((total: number, med: any) => {
      return total + this.getTimesArray(med.times).length;
    }, 0);
    
    const takenDoses = logs.length;
    const pendingDoses = Math.max(0, totalDoses - takenDoses);
    const overallPercentage = totalDoses > 0 ? Math.round((takenDoses / totalDoses) * 100) : 0;
    
    // Calcular por membro usando logsData (medication_logs_v2) com doses_detail JSONB
    const byMember = members.map((member: any) => {
      const memberMeds = medications.filter((m: any) => m.member_id === member.id);
      
      // Contar doses tomadas pelo membro usando doses_detail para maior precisão
      const memberLogs = logsData?.filter((log: any) => log.member_id === member.id) || [];
      let taken = 0;
      
      memberLogs.forEach((log: any) => {
        // Usar doses_detail se disponível (mais preciso)
        if (log.doses_detail && typeof log.doses_detail === 'object') {
          const dosesDetail = log.doses_detail;
          
          // Iterar sobre as doses no JSONB
          Object.values(dosesDetail).forEach((dose: any) => {
            if (dose && dose.taken_at) {
              const doseDate = convertUTCtoBRT(dose.taken_at);
              if (doseDate === today && (dose.status === 'taken' || dose.status === 'taken_late')) {
                taken++;
              }
            }
          });
        } else {
          // Fallback para contadores simples (compatibilidade)
          taken += (log.taken || 0) + (log.taken_late || 0);
        }
      });
      
      const total = memberMeds.reduce((sum: number, med: any) => sum + this.getTimesArray(med.times).length, 0);
      const percentage = total > 0 ? Math.round((taken / total) * 100) : 0;
      
      return {
        memberId: member.id,
        memberName: member.name,
        taken,
        total,
        percentage
      };
    });
    
    // Calcular aderência com status
    const adherenceByMember = byMember.map((m: any) => ({
      memberId: m.memberId,
      memberName: m.memberName,
      percentage: m.percentage,
      status: this.getAdherenceStatus(m.percentage, m.total)
    }));
    
    return {
      medication: {
        taken: takenDoses,
        total: totalDoses,
        percentage: overallPercentage,
        pending: pendingDoses,
        byMember
      },
      reminders: data.reminders,
      points: {
        earnedToday: data.pointsToday,
        totalFamily: data.familyPoints || 0  // Pontos de curiosidades
      },
      health: data.healthEvents,
      streaks: data.activeStreaks,
      quiz: data.quizStats,
      adherence: {
        overall: overallPercentage,
        byMember: adherenceByMember
      }
    };
  }

  private getAdherenceStatus(percentage: number, total: number): 'excellent' | 'good' | 'warning' | 'none' {
    if (total === 0) return 'none';
    if (percentage >= 90) return 'excellent';
    if (percentage >= 70) return 'good';
    return 'warning';
  }
}

export default DailyStatsService;


